import clsx from 'clsx'
import { ChevronsUpDown, Plus } from 'lucide-react'
import { type FieldError } from 'react-hook-form'
import { Show } from '@/components/show'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import type { SelectWithAddOption } from '@/types/select/select-with-add-option'
import { useSelectWithAddNew } from './hooks/use-select-with-add-new'

interface SelectWithAddNewProps {
  selectedValue: SelectWithAddOption
  options: SelectWithAddOption[]
  onChange: (value: SelectWithAddOption) => void
  canCreateNew?: boolean
  canSelect?: boolean
  error?: FieldError
}

export const SelectWithAddNew = ({
  selectedValue,
  options,
  onChange,
  canCreateNew,
  canSelect,
  error
}: SelectWithAddNewProps) => {
  const {
    states: { filter, term, open },
    actions: { handleChange, handleCreate, handleTerm, setOpen }
  } = useSelectWithAddNew({ options, onChange })

  const hasTerm = term.trim() !== ''

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        if (canSelect) setOpen(!open)
      }}
    >
      <PopoverTrigger asChild>
        <div>
          <Show when={canSelect}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={clsx(
                    'w-full flex overflow-hidden',
                    {
                      'text-foreground/50': !selectedValue.label
                    },
                    { 'border-destructive': error }
                  )}
                >
                  <div className="w-full overflow-hidden text-ellipsis text-start">
                    {selectedValue?.label || 'Selecione'}
                  </div>
                  <div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </div>
                </Button>
              </TooltipTrigger>
              <Show when={selectedValue.label !== ''}>
                <TooltipContent>
                  <p>{selectedValue.label}</p>
                </TooltipContent>
              </Show>
            </Tooltip>
          </Show>

          <Show when={!canSelect}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full h-12 flex items-center border-b border-ring/50">
                  <input
                    readOnly
                    value={selectedValue.label}
                    className="w-full text-base px-2 py-2 overflow-hidden text-ellipsis outline-none"
                  />
                </div>
              </TooltipTrigger>
              <Show when={selectedValue.label !== ''}>
                <TooltipContent>
                  <p>{selectedValue.label}</p>
                </TooltipContent>
              </Show>
            </Tooltip>
          </Show>
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Pesquisar..."
            value={term}
            onValueChange={handleTerm}
          />

          <CommandList>
            <CommandEmpty className="py-3 text-center italic text-black/50">
              Nenhum resultado encontrado
            </CommandEmpty>
            <CommandGroup>
              {filter.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => handleChange(option || '')}
                >
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <Show when={canCreateNew}>
            <div
              className={
                'px-2 select-none py-2 border-t border-ring flex justify-center'
              }
            >
              <Button
                type="button"
                disabled={!hasTerm}
                onClick={handleCreate}
                variant="light"
                className="w-auto max-w-full px-4 font-semibold"
              >
                <p>Adicionar</p>
                <Show when={hasTerm}>
                  <p className="truncate">"{term}"</p>
                </Show>
                <Plus className="text-[var(--brand-green)]" />
              </Button>
            </div>
          </Show>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
